import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import type { CreateBookingCancellationRequest, UpdateBookingRequest } from '@/shared/api';
import { bookingMutationErrorText, isStayCompleted } from './bookingMutationErrors';

// Update + Cancel fuer Manual-Buchungen aus dem Booking-Detail. Beide invalidieren die
// Detail-, Listen- und Calendar-Queries, damit Belegung/Liste sofort den neuen Stand zeigen –
// und die Rechnungen: Bearbeiten erneuert die Rechnung, Storno erzeugt die Stornorechnung.
function useInvalidateBooking(bookingId: string) {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['bookings'] }),
      qc.invalidateQueries({ queryKey: ['calendar'] }),
      qc.invalidateQueries({ queryKey: ['bookings', 'detail', bookingId] }),
      qc.invalidateQueries({ queryKey: ['invoice'] }),
    ]);
  };
}

export function useUpdateBooking(bookingId: string, onDone: () => void) {
  const api = useApi();
  const invalidate = useInvalidateBooking(bookingId);
  return useMutation({
    mutationFn: (body: UpdateBookingRequest) => api.bookings.update(bookingId, body),
    onSuccess: async () => {
      await invalidate();
      toast.success('Buchung aktualisiert');
      onDone();
    },
    onError: async (err) => {
      const problem = await toApiError(err);
      toast.error(bookingMutationErrorText(problem, 'Die Buchung konnte nicht geändert werden.'));
      // Über Mitternacht offen geblieben: neu laden, damit die Aktionen verschwinden.
      if (isStayCompleted(problem)) await invalidate();
    },
  });
}

export function useCancelBooking(bookingId: string, onDone: () => void) {
  const api = useApi();
  const invalidate = useInvalidateBooking(bookingId);
  return useMutation({
    mutationFn: (body: CreateBookingCancellationRequest) =>
      api.bookings.createCancellation(bookingId, body),
    networkMode: 'always',
    onSuccess: async () => {
      await invalidate();
      toast.success('Buchung storniert — Zeitraum wieder freigegeben');
      onDone();
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(
        e.status === 0
          ? 'Keine Antwort vom Server – ob storniert wurde, ist offen. Der Stand wird neu geladen.'
          : bookingMutationErrorText(e, 'Die Buchung konnte nicht storniert werden.'),
      );
      // Ein abgebrochenes Storno kann schon erstattet haben; die neu geladene Vorschau zeigt den
      // offenen Vorgang, und der Dialog setzt genau ihn fort.
      await invalidate();
    },
  });
}
